import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CellState } from '../types';
import type { TutorialStepData, TutorialLessonData } from '../types';
import { TUTORIAL_LESSONS } from '../tutorialData';
import { useGameStore } from '../store';
import { calculateRowClues, calculateColumnClues } from '../clues';
import { cluesFromLine, validateCell, cluesFromGrid } from '@org/nonogram-engine';
import { BoardGrid, type BoardParticle } from './BoardGrid';
import { ParallaxBg } from './ParallaxBg';
import { playFill, playMark, playWin, playTutorialWrong, playStepComplete, playClick, playModeSwitch, playPopup, playToggle, ensureAudioContext, onMusicToggle } from '../sounds';
import { trackEvent } from '../analytics';
import { Button, Popup, Heading, Muted } from './ui';
import { AudioSettings, SettingsGearIcon } from './SettingsPanel';
import tutorialLogo from '../../assets/tutorial_logo.png';
import './Tutorial.css';

/* ---------- helpers ---------- */

const EMPTY_SET = new Set<string>();
const EMPTY_NUM_SET = new Set<number>();

function cloneGrid(g: CellState[][]): CellState[][] {
  return g.map(r => [...r]);
}

/** Build grid with lesson presets + all actions from steps OTHER than stepIndex pre-filled. */
function buildGridForStep(lesson: TutorialLessonData, stepIndex: number): CellState[][] {
  const g: CellState[][] = Array.from({ length: lesson.size }, () =>
    Array(lesson.size).fill(CellState.Empty),
  );
  for (const a of lesson.preset ?? []) {
    g[a.row][a.col] = a.type === 'fill' ? CellState.Filled : CellState.Marked;
  }
  // Pre-fill actions from steps BEFORE the current one (already completed)
  for (let i = 0; i < stepIndex; i++) {
    const s = lesson.steps[i];
    if (s.action.type === 'tap') {
      for (const a of s.action.actions) {
        g[a.row][a.col] = a.type === 'fill' ? CellState.Filled : CellState.Marked;
      }
    }
  }
  return g;
}

function isLineDone(cells: CellState[], expectedClue: number[]): boolean {
  const actual = cluesFromLine(cells.map(c => c === CellState.Filled));
  return (
    actual.length === expectedClue.length &&
    actual.every((v, i) => v === expectedClue[i])
  );
}

function isPuzzleSolved(grid: CellState[][], solution: boolean[][]): boolean {
  for (let r = 0; r < solution.length; r++) {
    for (let c = 0; c < solution[r].length; c++) {
      if (solution[r][c] && grid[r][c] !== CellState.Filled) return false;
    }
  }
  return true;
}

/** Convert **bold** markers to <strong> JSX */
function renderText(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}

function calcCellSize(totalCols: number, totalRows: number): number {
  const boardSize = Math.min(window.innerWidth * 0.7, 280);
  const maxDim = Math.max(totalCols, totalRows); // force square aspect
  return Math.min(44, Math.max(16, Math.floor(boardSize / maxDim)));
}

function makePendingSet(step: TutorialStepData): Set<string> {
  if (step.action.type === 'tap') {
    return new Set(step.action.actions.map(a => `${a.row},${a.col},${a.type}`));
  }
  return new Set();
}

/* ---------- toolbar preview (completion screen) ---------- */

function ToolbarPreview({ onOpenSettings }: { onOpenSettings: () => void }) {
  const musicOn = useGameStore((s) => s.musicEnabled);
  const toggleMusic = useGameStore((s) => s.toggleMusic);

  return (
    <div className="nono-tutorial-toolbar-preview">
      <Button
        variant="secondary"
        size="circle"
        className="top-bar-btn"
        onClick={() => playClick()}
        aria-label="Guide"
      >
        ?
      </Button>
      <Button
        variant="secondary"
        size="circle"
        className="top-bar-btn"
        onClick={() => {
          ensureAudioContext();
          playToggle();
          const next = toggleMusic();
          onMusicToggle(next);
          trackEvent('music_toggle', { enabled: next });
        }}
        aria-label={musicOn ? 'Mute music' : 'Enable music'}
      >
        {'\u266A'}
        {!musicOn && <span className="top-bar-off-line" />}
      </Button>
      <Button
        variant="secondary"
        size="circle"
        className="top-bar-btn"
        onClick={onOpenSettings}
        aria-label="Settings"
      >
        <SettingsGearIcon />
      </Button>
    </div>
  );
}

/* ---------- component ---------- */

export function Tutorial({ startLesson, onLessonChange, onFinish }: {
  startLesson?: number;
  onLessonChange?: (lesson: number) => void;
  onFinish?: () => void;
}) {
  const lessons = TUTORIAL_LESSONS;
  const initialLesson = startLesson != null && startLesson >= 0 && startLesson < lessons.length ? startLesson : 0;

  const [lessonIndex, setLessonIndex] = useState(initialLesson);
  const [stepIndex, setStepIndex] = useState(0);
  const [grid, setGrid] = useState<CellState[][]>(() => buildGridForStep(lessons[initialLesson], 0));
  const [touchMode, setTouchMode] = useState<'fill' | 'mark'>(() => {
    const first = lessons[initialLesson].steps[0];
    if (first.action.type === 'tap' && first.action.actions.every(a => a.type === 'mark')) return 'mark';
    return 'fill';
  });
  const [pendingSet, setPendingSet] = useState<Set<string>>(() => makePendingSet(lessons[initialLesson].steps[0]));
  const [stepCompleting, setStepCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCells, setErrorCells] = useState<Set<string>>(EMPTY_SET);
  const [errorColorsMap, setErrorColorsMap] = useState<Map<string, string> | undefined>(undefined);
  const [lastAction, setLastAction] = useState({ row: -1, col: -1, type: '', id: 0 });
  const [solved, setSolved] = useState(false);
  const [particles, setParticles] = useState<BoardParticle[]>([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const lesson = lessons[lessonIndex];
  const step = lesson.steps[stepIndex];
  const isLastStep = stepIndex === lesson.steps.length - 1;
  const isLastLesson = lessonIndex === lessons.length - 1;

  // Clues
  const rowClues = useMemo(() => calculateRowClues(lesson.solution), [lesson.solution]);
  const colClues = useMemo(() => calculateColumnClues(lesson.solution), [lesson.solution]);
  const puzzleClues = useMemo(() => cluesFromGrid(lesson.solution), [lesson.solution]);

  // Completed rows/cols
  const completedRows = useMemo(
    () => Array.from({ length: lesson.size }, (_, r) => isLineDone(grid[r], rowClues[r])),
    [grid, rowClues, lesson.size],
  );
  const completedCols = useMemo(
    () => Array.from({ length: lesson.size }, (_, c) =>
      isLineDone(grid.map(row => row[c]), colClues[c]),
    ),
    [grid, colClues, lesson.size],
  );

  // Cell sizing
  const maxColClueLen = Math.max(...colClues.map(c => c.length));
  const maxRowClueLen = Math.max(...rowClues.map(c => c.length));
  const totalCols = maxRowClueLen + lesson.size;
  const totalRows = maxColClueLen + lesson.size;

  const [cellSize, setCellSize] = useState(() => calcCellSize(totalCols, totalRows));
  useEffect(() => {
    const handler = () => setCellSize(calcCellSize(totalCols, totalRows));
    handler();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [totalCols, totalRows]);

  // Pending actions helpers for tap steps
  const pendingFill = useMemo(() => {
    if (step.action.type !== 'tap') return EMPTY_SET;
    return new Set(
      [...pendingSet]
        .filter(k => k.endsWith(',fill'))
        .map(k => k.replace(/,fill$/, '')),
    );
  }, [step, pendingSet]);

  const pendingMark = useMemo(() => {
    if (step.action.type !== 'tap') return EMPTY_SET;
    return new Set(
      [...pendingSet]
        .filter(k => k.endsWith(',mark'))
        .map(k => k.replace(/,mark$/, '')),
    );
  }, [step, pendingSet]);

  const hasPendingMarks = step.action.type === 'tap' && step.action.actions.some(a => a.type === 'mark');
  const showModeSwitcher = lesson.showModeSwitcher || step.action.type === 'free' || hasPendingMarks;

  // Remaining cells for free mode
  const remainingCells = useMemo(() => {
    if (step.action.type !== 'free') return 0;
    let count = 0;
    for (let r = 0; r < lesson.solution.length; r++) {
      for (let c = 0; c < lesson.solution[r].length; c++) {
        if (lesson.solution[r][c] && grid[r][c] !== CellState.Filled) count++;
      }
    }
    return count;
  }, [step, grid, lesson.solution]);

  // Dynamic text for free steps
  const displayText = useMemo(() => {
    if (step.action.type !== 'free') return step.text;
    if (remainingCells === 0) return 'Puzzle complete!';
    return step.text;
  }, [step, remainingCells]);

  // --- Navigation ---

  const initStep = useCallback((s: TutorialStepData) => {
    setPendingSet(makePendingSet(s));
    setStepCompleting(false);
    setParticles([]);
    setError(null);
    setErrorCells(EMPTY_SET);
    setSolved(false);
    // Don't auto-switch touch mode — user toggles manually
  }, []);

  const goToLesson = useCallback((index: number) => {
    setLessonIndex(index);
    setStepIndex(0);
    setGrid(buildGridForStep(lessons[index], 0));
    initStep(lessons[index].steps[0]);
    // Set initial touch mode for the lesson's first step
    const first = lessons[index].steps[0];
    if (first.action.type === 'tap' && first.action.actions.every(a => a.type === 'mark')) {
      setTouchMode('mark');
    } else {
      setTouchMode('fill');
    }
    onLessonChange?.(index);
  }, [lessons, initStep, onLessonChange]);

  const advanceStep = useCallback(() => {
    if (isLastStep) {
      useGameStore.getState().saveTutorialLessonComplete(lessonIndex);
      if (isLastLesson) {
        setShowCompletion(true);
      } else {
        goToLesson(lessonIndex + 1);
      }
    } else {
      const nextIdx = stepIndex + 1;
      setStepIndex(nextIdx);
      setGrid(buildGridForStep(lesson, nextIdx));
      initStep(lesson.steps[nextIdx]);
    }
  }, [isLastStep, isLastLesson, lessonIndex, stepIndex, lesson, goToLesson, initStep, onFinish]);

  const handleNext = useCallback(() => {
    playClick();
    advanceStep();
  }, [advanceStep]);

  const handlePrevLesson = useCallback(() => {
    if (lessonIndex === 0) return;
    playClick();
    goToLesson(lessonIndex - 1);
  }, [lessonIndex, goToLesson]);

  // --- Error feedback ---

  const errorTimers = useRef<{ cell?: ReturnType<typeof setTimeout>; msg?: ReturnType<typeof setTimeout> }>({});

  const clearError = useCallback(() => {
    clearTimeout(errorTimers.current.cell);
    clearTimeout(errorTimers.current.msg);
    setError(null);
    setErrorCells(EMPTY_SET);
    setErrorColorsMap(undefined);
  }, []);

  const triggerError = useCallback((row: number, col: number, msg?: string, color?: string) => {
    // Cancel previous timers
    clearTimeout(errorTimers.current.cell);
    clearTimeout(errorTimers.current.msg);

    playTutorialWrong();
    setError(msg ?? 'Not quite \u2014 try another cell!');
    const key = `${row},${col}`;
    setErrorCells(new Set([key]));
    setErrorColorsMap(color ? new Map([[key, color]]) : undefined);
    errorTimers.current.cell = setTimeout(() => {
      setErrorCells(EMPTY_SET);
      setErrorColorsMap(undefined);
    }, 1500);
    // Long fallback — error stays until next cell interaction or 8s
    errorTimers.current.msg = setTimeout(() => {
      setError(null);
    }, 8000);
  }, []);

  // --- Cell interaction ---

  const handleCellDown = useCallback((row: number, col: number, button: number): boolean => {
    if (stepCompleting || solved) return false;

    const isRight = button === 2 || touchMode === 'mark';
    const type = isRight ? 'mark' : 'fill';

    // No interaction for 'next' steps
    if (step.action.type === 'next') return false;

    // Already filled/marked cells — skip in tap mode
    if (step.action.type === 'tap') {
      if (grid[row][col] !== CellState.Empty) return false;

      const key = `${row},${col},${type}`;
      if (!pendingSet.has(key)) {
        const override = step.errorOverrides?.find(e => e.row === row && e.col === col);
        if (override) {
          triggerError(row, col, override.message, override.color);
        } else {
          const result = validateCell(row, col, lesson.solution, puzzleClues);
          triggerError(row, col, result.message ?? undefined);
        }
        return false;
      }

      // Correct action
      const ng = cloneGrid(grid);
      ng[row][col] = type === 'fill' ? CellState.Filled : CellState.Marked;
      setGrid(ng);
      setLastAction({ row, col, type, id: Date.now() });

      const newPending = new Set(pendingSet);
      newPending.delete(key);
      setPendingSet(newPending);
      clearError();

      if (type === 'fill') playFill(); else playMark();

      if (newPending.size === 0) {
        setStepCompleting(true);

        // Check if the whole puzzle is now solved
        const puzzleDone = isPuzzleSolved(ng, lesson.solution);

        if (puzzleDone) {
          playStepComplete();
          // Confetti particles from all filled cells
          const confetti: BoardParticle[] = [];
          const colors = ['#f0b830', '#e8a040', '#ffd060'];
          let pid = 0;
          for (let r = 0; r < lesson.size; r++) {
            for (let c = 0; c < lesson.size; c++) {
              if (!lesson.solution[r][c]) continue;
              const cx = c + 0.5;
              const cy = r + 0.5;
              const count = 3 + Math.floor(Math.random() * 2);
              for (let i = 0; i < count; i++) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 2 + Math.random() * 2;
                confetti.push({
                  id: pid++,
                  x: cx, y: cy,
                  dx: Math.cos(angle) * speed,
                  dy: Math.sin(angle) * speed - 2,
                  color: colors[Math.floor(Math.random() * colors.length)],
                  delay: r * 60 + c * 30,
                });
              }
            }
          }
          setParticles(confetti);
        }

        setTimeout(() => {
          setStepCompleting(false);
          advanceStep();
        }, puzzleDone ? 800 : 400);
      }
      return true;
    }

    // Free mode
    if (step.action.type === 'free') {
      if (type === 'fill') {
        if (grid[row][col] === CellState.Filled) return false;
        if (grid[row][col] === CellState.Marked) {
          // Remove mark in fill mode
          const ng = cloneGrid(grid);
          ng[row][col] = CellState.Empty;
          setGrid(ng);
          setLastAction({ row, col, type: 'mark', id: Date.now() });
          playMark();
          return true;
        }
        // Validate against solution
        if (lesson.solution[row][col]) {
          const ng = cloneGrid(grid);
          ng[row][col] = CellState.Filled;
          setGrid(ng);
          setLastAction({ row, col, type: 'fill', id: Date.now() });
          clearError();
          playFill();

          // Check if puzzle is solved
          if (isPuzzleSolved(ng, lesson.solution)) {
            playWin();
            setSolved(true);
            setTimeout(() => advanceStep(), 1000);
          }
          return true;
        } else {
          const result = validateCell(row, col, lesson.solution, puzzleClues);
          triggerError(row, col, result.message ?? 'That cell should be empty!');
          return false;
        }
      } else {
        // Mark mode — toggle marks freely
        if (grid[row][col] === CellState.Filled) return false;
        const ng = cloneGrid(grid);
        ng[row][col] = ng[row][col] === CellState.Marked ? CellState.Empty : CellState.Marked;
        setGrid(ng);
        setLastAction({ row, col, type: 'mark', id: Date.now() });
        playMark();
        return true;
      }
    }
    return false;
  }, [
    step, grid, pendingSet, touchMode, stepCompleting, solved, lesson,
    rowClues, colClues, completedRows, completedCols, triggerError, advanceStep,
  ]);

  // --- Highlight cells for current step ---

  const highlightCellsSet = useMemo(() => {
    if (!step.highlightCells?.length) return undefined;
    return new Set(step.highlightCells.map(c => `${c.row},${c.col}`));
  }, [step]);

  /* ---------- Completion screen (after all lessons) ---------- */

  if (showCompletion) {
    return (
      <div className="nono-tutorial">
        <ParallaxBg />

        <div className="nono-tutorial-logo">
          <span className="nono-tutorial-welcome">Welcome to</span>
          <img className="nono-tutorial-brand-img" src={tutorialLogo} alt="Nonogram Daily" draggable={false} />
          <span className="nono-tutorial-label">Tutorial</span>
        </div>

        <h2 className="nono-tutorial-lesson-title">You're all set!</h2>

        <div className="nono-tutorial-heading">
          <p>{renderText('You\'ll always find these in the **top bar** during every game.')}</p>
        </div>

        <ToolbarPreview onOpenSettings={() => { playPopup(); setShowSettings(true); }} />

        <div className="nono-tutorial-text">
          <p>{renderText('**Help**, **music**, and **settings** — adjust sound and game options anytime!')}</p>
        </div>

        <Button variant="primary" className="nono-tutorial-next-btn" onClick={() => { playClick(); onFinish?.(); }}>
          Finish
        </Button>

        {showSettings && (
          <Popup aria-label="Settings" onDismiss={() => setShowSettings(false)} maxWidth={380}>
            <div className="top-bar-settings">
              <Heading>Settings</Heading>
              <Muted className="top-bar-settings-sub">
                Adjust audio and puzzle display.
              </Muted>
              <div className="top-bar-settings-group">
                <div className="top-bar-settings-section">
                  <div className="top-bar-settings-section-title">Audio</div>
                  <AudioSettings />
                </div>
              </div>
            </div>
          </Popup>
        )}
      </div>
    );
  }

  /* ---------- Lesson screen ---------- */

  return (
    <div className="nono-tutorial">
      <ParallaxBg />

      {/* Logo header */}
      <div className="nono-tutorial-logo">
        <span className="nono-tutorial-welcome">Welcome to</span>
        <img className="nono-tutorial-brand-img" src={tutorialLogo} alt="Nonogram Daily" draggable={false} />
        <span className="nono-tutorial-label">Tutorial</span>
      </div>

      {/* Top row: prev/restart + counter */}
      <div className="nono-tutorial-toprow">
        {lessonIndex > 0 ? (
          <button className="nono-tutorial-prev" onClick={handlePrevLesson}>
            {'\u2190'} Previous lesson
          </button>
        ) : (
          <button className="nono-tutorial-prev" onClick={() => { playClick(); goToLesson(lessonIndex); }}>
            {'\u21BB'} Restart lesson
          </button>
        )}
        <span className="nono-tutorial-counter">
          {lessonIndex + 1} / {lessons.length}
        </span>
      </div>

      {/* Lesson title */}
      <h2 className="nono-tutorial-lesson-title">{lesson.title}</h2>

      {/* Heading (optional, above board) */}
      {step.heading && (
        <div className="nono-tutorial-heading">
          <p>{renderText(step.heading)}</p>
        </div>
      )}

      {/* Board */}
      <div className="nono-tutorial-board">
        <BoardGrid
          size={lesson.size}
          inputMode={touchMode}
          rowClues={rowClues}
          colClues={colClues}
          cellSize={cellSize}
          grid={grid}
          completedRows={completedRows}
          completedCols={completedCols}
          errorCells={errorCells}
          errorColors={errorColorsMap}
          flashRows={EMPTY_NUM_SET}
          flashCols={EMPTY_NUM_SET}
          particles={particles}
          highlightCells={highlightCellsSet}
          showCrosshair={false}
          focusRow={-1}
          focusCol={-1}
          solved={solved}
          solution={lesson.solution}
          lastAction={lastAction}
          onCellDown={handleCellDown}
          dragRepeatsDown
        />
      </div>

      {/* Fill / Mark mode toggle (same as regular game) */}
      {showModeSwitcher && (
        <div className="game-mode-toggle" role="group" aria-label="Input mode" data-mode={touchMode}>
          <span className="game-mode-toggle__thumb" aria-hidden="true" />
          <button
            className={`game-mode-toggle__option game-mode-toggle__option--fill${touchMode === 'fill' ? ' active' : ''}`}
            onClick={() => { if (touchMode !== 'fill') playModeSwitch(); setTouchMode('fill'); }}
            aria-label="Fill mode"
          >
            <span className="game-mode-toggle__fill-swatch" aria-hidden="true" />
          </button>
          <button
            className={`game-mode-toggle__option game-mode-toggle__option--mark${touchMode === 'mark' ? ' active' : ''}`}
            onClick={() => { if (touchMode !== 'mark') playModeSwitch(); setTouchMode('mark'); }}
            aria-label="Mark mode"
          >
            {'\u2715'}
          </button>
        </div>
      )}

      {/* Instruction text (below board/toggle) */}
      <div className="nono-tutorial-text">
        <p>{renderText(displayText)}</p>
      </div>

      {/* Error feedback */}
      {error && <div className="nono-tutorial-error">{error}</div>}

      {/* Next / Finish button */}
      {step.action.type === 'next' && (
        <Button variant="primary" className="nono-tutorial-next-btn" onClick={handleNext}>
          Next
        </Button>
      )}
    </div>
  );
}
